import { IonAvatar, IonBadge, IonButton, IonButtons, IonCardSubtitle, IonCol, IonContent, 
    IonFooter, IonHeader, IonIcon, IonImg, IonItem, IonItemOption, IonItemOptions, 
    IonItemSliding, IonLabel, IonList, IonNote, IonPage, IonRadioGroup, IonRadio,
    IonRow, IonTitle, IonToolbar, useIonToast, IonInput, IonSelect, IonSelectOption,
    IonGrid, IonTextarea, IonSegment, IonSegmentButton,
    IonItemDivider} from "@ionic/react";
import { cart, cartOutline, checkmarkSharp, chevronBackOutline, trashOutline, alertSharp,
    add, remove, closeOutline,
    toggle
 } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { CartStore, updateCartItem, removeFromCart, cleanCart } from "../data/CartStore";
import { ProductStore } from "../data/ProductStore";

import styles from "./CartProducts.module.css";
import Product from "./Product";
import { update } from "pullstate";

import { coreLocations, postOfficeLocations } from '../data';
import { set } from "date-fns";
import { encodeId } from "../utils/encoding";

const CartProducts = () => {

    const history = useHistory();

    const cartRef = useRef();
    const products = ProductStore.useState(s => s.products);
    const productsFullyLoaded = ProductStore.useState(s => s.fullyLoaded);
    const shopCart = CartStore.useState(s => s.product_ids);
    const [cartProducts, setCartProducts] = useState([]);
    const [amountLoaded, setAmountLoaded] = useState(6);

    let productStocks = useRef([]);

    const [presentToast] = useIonToast();

    const [total, setTotal] = useState(0);

    // This effect now ONLY cleans the cart and runs just once when products are loaded.
    useEffect(() => {
        if (productsFullyLoaded) {
            cleanCart();
        }
    }, [productsFullyLoaded]);

    // This effect safely computes the displayable cart products whenever the (now clean) shopCart changes.
    useEffect(() => {
        if (productsFullyLoaded) {
            let newTotal = 0;
            const newCartProducts = shopCart.map((product, index) => {
                const { categorySlug, productID, newDeliveryLocID, dzo, address, price, data, quantity, note, deliveryMode, attributes } = product;

                const tempCategory = products.find(p => p.slug === categorySlug);
                const tempProduct = tempCategory.products.find(p => parseInt(p.id) === parseInt(productID));

                // This check is for safety, but cleanCart should prevent this from happening.
                if (!tempProduct) {
                    return null;
                }

                newTotal += (tempProduct.price * quantity);
                if (price) {
                    newTotal += parseInt(price);
                }

                // Store stock in a ref to avoid re-renders
                productStocks.current[index] = tempProduct.stock;

                return {
                    slugId: `${categorySlug}-${productID}-${index}`, // Make key even more unique for items that can be in cart twice
                    category: tempCategory,
                    product: tempProduct,
                    selected_location: newDeliveryLocID ? parseInt(newDeliveryLocID) : null,
                    selected_dzo: dzo,
                    address: address,
                    delPrice: price,
                    data: data,
                    attributes: attributes,
                    days: data?.days,
                    quantity: quantity,
                    note: note,
                    deliveryMode: deliveryMode
                };
            }).filter(Boolean); // Filter out any null items

            setTotal(newTotal);
            setCartProducts(newCartProducts);
        }
    }, [shopCart, productsFullyLoaded, products]);

    const fetchMore = async (e) => {

        //	Increment the amount loaded by 6 for the next iteration
        setAmountLoaded(prevAmount => (prevAmount + 6));
        e.target.complete();
    }

    const removeProductFromCart = async (index) => {

        const product = cartProducts[index];
        if (!product) return;

        const slidingItem = document.querySelector(`#sliding-${product.id}`);
        if (slidingItem) {
            await slidingItem.closeOpened();
        }

        removeFromCart(index);
    }

    const toggleSlidingItem = async (id, action) => {

        const slidingItem = document.querySelector(`#sliding-${id}`);
        if (slidingItem) {
            if(action === 'open'){
                await slidingItem.open('end');
            } else {
                await slidingItem.close();
            }
        }
    }

    // Check if all delivery locations are selected before proceeding to checkout
    const handleCheckout = () => {

        const isDeliveryLocationSelected = cartProducts.map(product => product.selected_location !== null);

        if (isDeliveryLocationSelected.every(Boolean)) {
            history.push('/checkout');
        } else {
            presentToast({
				message: 'Please select either a pickup or a delivery location for every products.',
				icon: alertSharp,
				color: 'danger',
				buttons: [
					{
						text: 'Dismiss',
						role: 'cancel'
					}
				]
			  });
        }
    };
    
    const handleFreeLocations = (index, location) => {
        let item = shopCart[index];
        updateCartItem(index, location, null, null, null, item.data, item.quantity, item.note, 'pickup');
    };

    const handleAddress = (index, address) => {
        let item = shopCart[index];
        updateCartItem(index, null, item.dzo, address, item.price, item.data, item.quantity, item.note, 'delivery');
    };
    const handlePaidLocations = (index, dzongkhag, delLocID, price, data) => {
        let item = shopCart[index];
        data = {...item.data, ...data}; // Merge existing data with new data from location
        updateCartItem(index, delLocID, dzongkhag, null, price, data, item.quantity, item.note, 'delivery');
    }

    const updateQuantity = (index, qty) => {
        let item = shopCart[index];
        let newQty = Math.max(qty, 1);
        newQty = Math.min(newQty, productStocks[index]);
        updateCartItem(index, item.newDeliveryLocID, item.dzo, item.address, item.price, item.data, newQty, item.note, item.deliveryMode);
    }

    const incrementQuantity = (index) => {
        let item = shopCart[index];
        let newQty = Math.max(item.quantity+1, 1);
        newQty = Math.min(newQty, productStocks[index]);
        updateCartItem(index, item.newDeliveryLocID, item.dzo, item.address, item.price, item.data, newQty, item.note, item.deliveryMode);
    };

    const decrementQuantity = (index) => {
        let item = shopCart[index];
        let newQty = Math.max(item.quantity - 1, 1);
        newQty = Math.min(newQty, productStocks[index]);
        updateCartItem(index, item.newDeliveryLocID, item.dzo, item.address, item.price, item.data, newQty, item.note, item.deliveryMode);
    };

    const handleNote = (index, note) => {
        let item = shopCart[index];
        updateCartItem(index, item.newDeliveryLocID, item.dzo, item.address, item.price, item.data, item.quantity, note, item.deliveryMode);
    };

    const handleAttributes = (index, attribute) => {
        let item = shopCart[index];
        let attributes = { ...item.attributes, ...attribute }; // Merge existing attributes with the new one
        updateCartItem(index, item?.newDeliveryLocID, item?.dzo, item?.address, item?.price, item?.data, item?.quantity, item?.note, item?.deliveryMode, attributes);
    };

    // Function to process delivery locations and return select options
    function getSelectOptions(deliveryLocations) {
        let postOffCharge = -1; let dispatchDays = -1; let poLocID = null;
        let locOptions = [];

        locOptions = deliveryLocations.filter(delivery_loc => delivery_loc.charges > 0).flatMap(dzo => {
            const locationsArray = JSON.parse(dzo.location); // Parse the JSON string to an array
            return locationsArray.map((coreLocKey, clIdx) => {
                const label = Object.entries(coreLocations).find(([key, value]) => key === coreLocKey)?.[1];
                if (coreLocKey && coreLocKey.includes('post_office')) {
                    postOffCharge = dzo.charges;
                    dispatchDays = JSON.parse(dzo.data)?.days;
                    poLocID = dzo.id;
                    return null;
                } else {
                    return {
                        key: `option-${dzo.id}-${clIdx}`,
                        value: coreLocKey,
                        label: label,
                        delLocID: dzo.id,
                        price: dzo.charges,
                        data: JSON.parse(dzo.data)
                    };
                }
            }).filter(item => item !== null);
        });
        if (postOffCharge > -1) { 
            Object.entries(postOfficeLocations).forEach(([key, value], idx) => {
                locOptions.push({
                    key: `option-${key}-${idx}`,
                    value: key,
                    label: 'Pickup at ' + value, // Use the value as the display label
                    delLocID: poLocID,
                    price: postOffCharge,
                    data: { days: dispatchDays }
                });
            });
        }
        return locOptions;
    }

    function setDeliveryMode(index, mode) {
        let item = shopCart[index];
        updateCartItem(index, item.newDeliveryLocID, null, null, 0, item.data, item.quantity, item.note, mode);
    }

    return (

        <IonPage id="category-page" className={styles.categoryPage}>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="dark" routerLink="/" routerDirection="back">
                            <IonIcon color="dark" icon={chevronBackOutline} />&nbsp;Home
                        </IonButton>
                    </IonButtons>
                    <IonTitle>Cart</IonTitle>

                    <IonButtons slot="end">
                        <IonBadge color="dark">
                            {shopCart.length}
                        </IonBadge>
                        <IonButton color="dark">
                            <IonIcon ref={cartRef} className="animate__animated" icon={cart} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="page-content">

                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="dark" routerLink="/" routerDirection="back">
                            <IonIcon color="dark" icon={chevronBackOutline} />&nbsp;Home
                        </IonButton>
                    </IonButtons>
                    <IonTitle>Cart</IonTitle>

                    <IonButtons slot="end" class="ion-padding-left">
                        <IonButton color="drukre" routerLink="/cart">
							{ shopCart.length }
							<IonIcon ref={ cartRef } className="animate__animated" icon={ cartOutline } />
						</IonButton>
					</IonButtons>
                </IonToolbar>

                <IonRow className="ion-text-center">
                    <IonCol size="12" className="ion-padding-horizontal">
                        <p>{cartProducts && cartProducts.length} {(cartProducts.length > 1 || cartProducts.length === 0) ? " products" : " product"}</p>
                        <IonNote>If you need to remove an item, please swipe left on the item and click the trash icon.</IonNote>
                    </IonCol>
                </IonRow>

                <IonList>
                    {cartProducts && cartProducts.map((product, index) => {
                        productStocks[index] = product.product.stock;
                        //if ((index <= amountLoaded)) {
                            return (
                                <IonItemSliding key={product.slugId} id={`sliding-${product.slugId}`} className={styles.cartSlider}>
                                    <IonItem key={`item-${product.slugId}`}>
                                        <IonGrid>
                                            <IonRow key={`sliding1-${product.slugId}`}>
                                                <IonCol size-xl="5" size-lg="5" size-md="5" size-sm="12" size-xs="12">
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <IonItem key={`sliding2-${product.slugId}`}lines="none" detail={false} className={styles.cartItem}>
                                                                <a href={`/category/${product.category.slug}/${encodeId(product.product.id)}`} title="Go to item">
                                                                    <IonAvatar>
                                                                        <IonImg src={product.product.images?.length > 0 ? `${process.env.REACT_APP_PRODUCT_ASSETS}/${product.product.images[0]}` : `${process.env.PUBLIC_URL}/assets/no_image_available.jpg`} />
                                                                    </IonAvatar>
                                                                </a>
                                                                <IonLabel className="ion-padding-start ion-text-wrap">
                                                                    <p>{product.category.name}</p>
                                                                    <h4>{product.product.name}</h4>
                                                                </IonLabel>

                                                                <div className={styles.cartActions}>
                                                                    <IonBadge color="danger">Nu. {product.product.price}</IonBadge>
                                                                </div>
                                                            </IonItem>
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        {product.product.data && (
                                                            Object.keys(product.product.data).filter(key => product.product.data[key] === true).length > 0 && (
                                                                <IonCol size="12" key="boolean-data">
                                                                    {Object.keys(product.product.data).filter(key => product.product.data[key] === true).length === 1 ? (
                                                                        <IonInput label={Object.keys(product.product.data).filter(key => product.product.data[key] === true)[0].toUpperCase()} labelPlacement="floating" fill="outline" value={product.product.data[Object.keys(product.product.data).filter(key => product.product.data[key] === true)[0]] ? 'Yes' : 'No'} disabled></IonInput>
                                                                    ) : (
                                                                        <IonRadioGroup label="Product Details" labelPlacement="floating" fill="outline" horizontal
                                                                            value={product?.attributes?.main_attribute || null}
                                                                            onIonChange={(e) => handleAttributes(index, { 'main_attribute': e.target.value })}
                                                                        >
                                                                            <IonRow>
                                                                            {Object.keys(product.product.data).map((key, attrIndex) => (
                                                                                product.product.data[key] === true && product.product.data[key] !== false && typeof product.product.data[key] !== 'object' && (    
                                                                                    <IonCol key={attrIndex} sizeSm="4" sizeXs="4" sizeMd="4" sizeLg="3" sizeXl="3">
                                                                                        <IonRadio key={attrIndex} value={key}>{key.toUpperCase()}</IonRadio>
                                                                                    </IonCol>
                                                                                )
                                                                            ))}
                                                                            </IonRow>
                                                                        </IonRadioGroup>
                                                                    )}
                                                                </IonCol>
                                                            )
                                                        )}
                                                        {product.product.data && Object.keys(product.product.data).map((key, attrIndex) => (
                                                            product.product.data[key] && product.product.data[key].length > 0 && (
                                                                <IonCol size="3" key={attrIndex}>
                                                                    {(product.product.data[key].length === 1 || typeof product.product.data[key] === 'string') ? (
                                                                        <IonInput label={key.toUpperCase()} labelPlacement="floating" fill="outline" value={product.product.data[key]} disabled></IonInput>
                                                                    ) : (
                                                                        <IonSelect label={key.toUpperCase()} labelPlacement="floating" fill="outline" key={attrIndex}
                                                                            value={product.attributes ? product.attributes[key] : null}
                                                                            onIonChange={(e) => handleAttributes(index, { [key]: e.target.value })}
                                                                        >
                                                                            {product.product.data[key].map((item, subIndex) => (
                                                                                <IonSelectOption style={{ backgroundColor: '#3880ff', borderRadius: '3px', margin: '3px', padding: '5px' }} key={`${index}-${subIndex}`} value={item}>
                                                                                    {item}
                                                                                </IonSelectOption>
                                                                            ))}
                                                                        </IonSelect>
                                                                    )}
                                                                </IonCol>
                                                            )
                                                        ))}
                                                </IonRow>
                                                </IonCol>

                                                <IonCol size-xl="2" size-lg="2" size-md="2" size-sm="12" size-xs="12">
                                                    <IonNote>Quantity</IonNote>
                                                    <IonInput labelPlacement="stacked" value={product.quantity}
                                                        onIonInput={(e) => updateQuantity(index, Number(e.target.value))} className="ion-text-center">
                                                        <IonButton slot="start" aria-label="Subtract" onClick={() => decrementQuantity(index)} >
                                                            <IonIcon icon={remove}></IonIcon>
                                                        </IonButton>
                                                        <IonButton slot="end" aria-label="Add" onClick={() => incrementQuantity(index)} >
                                                            <IonIcon icon={add}></IonIcon>
                                                        </IonButton>
                                                    </IonInput>
                                                </IonCol>
                                                <IonCol size-xl="5" size-lg="5" size-md="5" size-sm="12" size-xs="12">
                                                    <IonSegment value={product.deliveryMode || 'pickup'} onIonChange={e => {setDeliveryMode(index, String(e.detail.value) || 'pickup'); }}>
                                                        <IonSegmentButton value="pickup" checked={product.deliveryMode === 'pickup' || !product.deliveryMode}>
                                                            <IonLabel>Free Pickup</IonLabel>
                                                        </IonSegmentButton>
                                                        {product.product.delivery_locations.some(delivery_loc => delivery_loc.charges > 0) && (
                                                        <IonSegmentButton value="delivery" checked={product.deliveryMode === 'delivery'}>
                                                            <IonLabel>Paid Pickup/Delivery</IonLabel>
                                                        </IonSegmentButton>
                                                        )}
                                                    </IonSegment>
                                                    {(product.deliveryMode === 'pickup' || !product.deliveryMode)
                                                        && product.product.delivery_locations.some(delivery_loc => delivery_loc.charges < 1) && (
                                                        <div className="ion-padding">
                                                            <IonNote className="ion-padding-start">Free Pickup Location</IonNote>
                                                            <IonRadioGroup labelPlacement="floating" fill="outline" value={product.selected_location} onIonChange={e => handleFreeLocations(index, e.detail.value)}>
                                                                {product.product.delivery_locations.filter(delivery_loc => delivery_loc.charges < 1).map((delivery_loc, ii) => (
                                                                    <IonItem className="ion-padding-start" key={`item-${index}-${ii}`}>
                                                                        <IonLabel>{delivery_loc.location} <IonNote> {JSON.parse(delivery_loc.data)?.days ? ` - Pickup ready within ${JSON.parse(delivery_loc.data)?.days} day(s)` : null}</IonNote></IonLabel>
                                                                        <IonRadio slot="end" value={delivery_loc.id} />
                                                                    </IonItem>
                                                                ))}
                                                            </IonRadioGroup>
                                                        </div>
                                                    )}
                                                    {product.deliveryMode === 'delivery' && product.product.delivery_locations.some(delivery_loc => delivery_loc.charges > 0) && (
                                                     <div className="ion-padding">
                                                        <IonNote className="ion-padding-start">Pickup/Delivery on Extra Cost</IonNote>
                                                        <IonRow key={`sliding4-${index}`}>
                                                            <IonCol size="4">
                                                                <IonSelect 
                                                                    value={product.selected_dzo} onIonChange={e => {
                                                                        const selectedOption = getSelectOptions(product.product.delivery_locations).find(option => option.value === e.detail.value);
                                                                        if (selectedOption) {
                                                                            handlePaidLocations(index, e.detail.value, selectedOption.delLocID, selectedOption.price, selectedOption.data);
                                                                        }
                                                                    }} 
                                                                    labelPlacement="floating" fill="outline" 
                                                                    placeholder="Select Location"
                                                                >
                                                                    <IonSelectOption value={null}>Select Locality</IonSelectOption>
                                                                    {getSelectOptions(product.product.delivery_locations).map(({ key, value, label, delLocID, price, data }) => (
                                                                        <IonSelectOption key={key} value={value}>
                                                                            {label}
                                                                        </IonSelectOption>
                                                                    ))}
                                                                </IonSelect>
                                                            </IonCol>
                                                            <IonCol size="8">
                                                                <IonInput value={product.address} 
                                                                    placeholder="Enter address here" type="text" 
                                                                    disabled={product.selected_dzo == null || (typeof product.selected_dzo === 'string' && product.selected_dzo.includes('post_office'))} 
                                                                    onIonInput={e => {
                                                                        // You need to have delLocID and price available here, possibly from component state
                                                                        handleAddress(index, e.detail.value);
                                                                    }} 
                                                                    labelPlacement="floating" fill="outline"
                                                                    label="Enter Your Address">
                                                                </IonInput>
                                                            </IonCol>
                                                        </IonRow>
                                                        <IonRow key={`sliding6-${index}`}>
                                                            <IonCol size="6">
                                                                {product.days ? <>Pickup/Delivery Time : <IonNote>Within {product.days} day(s)</IonNote></> : null}
                                                            </IonCol>
                                                            <IonCol size="6">
                                                                {product.delPrice ?
                                                                    <>
                                                                        Pickup/Delivery Charge &nbsp;<IonBadge className="ion-float-right" color="danger">Nu. {product.delPrice}</IonBadge>
                                                                    </>
                                                                : null}
                                                            </IonCol>
                                                        </IonRow>
                                                        {typeof product.selected_dzo === 'string' && product.selected_dzo.includes('post_office') && (
                                                            <div className="ion-padding-start">
                                                                <IonNote>Note: Pickups from post offices can depend on the postal services.</IonNote>
                                                            </div>
                                                        )}
                                                    </div>
                                                    )}
                                                    {product.product.delivery_locations.length < 1 ? 
                                                        <IonLabel className="ion-padding-start"> - No pickup locations</IonLabel>
                                                    : null}
                                                </IonCol>
                                                <IonCol size-md="8" offset-md="2">
                                                    <IonNote className="ion-padding-start">Note to Seller</IonNote>
                                                    <IonRow>
                                                        <IonCol size="12">
                                                            <IonTextarea label="Note to Seller" labelPlacement="floating" fill="outline" 
                                                                placeholder="A note can be anything such as product specs, gifting note, etc..."
                                                                value={product.note} onIonInput={(e) => handleNote(index, e.target.value)}
                                                                counter={true}
                                                                maxlength={200}
                                                                autoGrow={true}
                                                                counterFormatter={(inputLength, maxLength) => `${maxLength - inputLength} characters remaining`}
                                                            ></IonTextarea>
                                                        </IonCol>
                                                    </IonRow>
                                                </IonCol>
                                                <IonCol size="12" className="ion-text-center ion-padding-bottom">
                                                    <IonButton color="danger" fill="clear" onClick={() => toggleSlidingItem(product.slugId, 'open')}>
                                                        <IonIcon icon={trashOutline} />&nbsp;Delete
                                                    </IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonGrid>
                                    </IonItem>
                                    <IonItemOptions side="end">
                                        <IonItemOption color="danger" style={{ paddingLeft: "1rem", paddingRight: "1rem" }} 
                                            onClick={() => removeProductFromCart(index)}>
                                            <IonIcon icon={trashOutline} />
                                        </IonItemOption>
                                        <IonItemOption color="medium" style={{ paddingLeft: "1rem", paddingRight: "1rem" }} 
                                            onClick={() => toggleSlidingItem(product.slugId, 'close')}>
                                            <IonIcon icon={closeOutline} />
                                        </IonItemOption>
                                    </IonItemOptions>
                                </IonItemSliding>
                            );
                        //}
                    })}
                </IonList>
            </IonContent>

            <IonFooter>
                <div className={styles.cartCheckout}>
                    <IonCardSubtitle>Nu. {total.toFixed(2)}</IonCardSubtitle>
                    <IonNote className="ion-padding-end ion-padding-start">GST Inclusive. Currency converted based on chosen payment method.</IonNote>
                    <IonButton color="drukre" onClick={() => handleCheckout()} disabled={cartProducts.length < 1}>
                        <IonIcon icon={checkmarkSharp} />&nbsp;Checkout
                    </IonButton>
                </div>
            </IonFooter>
        </IonPage>
    );
}

export default CartProducts;